import { DatepickerSelectedDateType } from "@website/ui/datepicker";
import { dateFormat } from "@website/utils/date-format";
import toNumber from "lodash-es/toNumber";
import toString from "lodash-es/toString";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import { HotelDomesticHotelDataType } from "./index.types";

export const useHotelSearch = (hotelData?: HotelDomesticHotelDataType) => {
  const router = useRouter();
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);

  const { date_to, date_from, adults, children, no_rooms } =
    router.query as Record<string, string>;
  const datePickerInitialValues: DatepickerSelectedDateType = useMemo(
    () => [
      {
        year: Number(
          dateFormat(date_from, {
            locale: router.locale,
            numberConvert: false,
            template: "yyyy"
          })
        ),
        month: Number(
          dateFormat(date_from, {
            locale: router.locale,
            numberConvert: false,
            template: "MM"
          })
        ),
        day: Number(
          dateFormat(date_from, {
            locale: router.locale,
            numberConvert: false,
            template: "dd"
          })
        ),
        dateTime: toString(
          dateFormat(date_from, {
            locale: "en",
            template: "yyyy-MM-dd"
          })
        )
      },
      {
        year: Number(
          dateFormat(date_to, {
            locale: router.locale,
            numberConvert: false,
            template: "yyyy"
          })
        ),
        month: Number(
          dateFormat(date_to, {
            locale: router.locale,
            numberConvert: false,
            template: "MM"
          })
        ),
        day: Number(
          dateFormat(date_to, {
            locale: router.locale,
            numberConvert: false,
            template: "dd"
          })
        ),
        dateTime: toString(
          dateFormat(date_to, {
            locale: "en",
            template: "yyyy-MM-dd"
          })
        )
      }
    ],
    [date_from, date_to, router]
  );

  const passengerCountInitialValues = {
    adult: toNumber(adults),
    child: toNumber(children),
    room: toNumber(no_rooms)
  };

  const handleSearchSubmit = useCallback(
    (
      destinationURL: string,
      date_from: string,
      date_to: string,
      adults: string,
      children: string,
      no_rooms: string
    ) =>
      router
        .replace(
          {
            pathname: "/[cityTitle]/[hotelTitle]",
            query: {
              ...router.query,
              date_from,
              date_to,
              adults,
              children,
              no_rooms
            }
          },
          undefined,
          { shallow: true, scroll: false }
        )
        .finally(() => setIsSearchPanelOpen(false)),
    [router]
  );

  const handleCloseSearchPanel = useCallback(() => {
    setIsSearchPanelOpen(false);
  }, []);

  return {
    isSearchPanelOpen,
    setIsSearchPanelOpen,
    searchSummaryProperties: {
      cityTitle: hotelData?.title,
      dateFrom: date_from,
      dateTo: date_to,
      adults,
      children,
      noRooms: no_rooms
    },
    searchPanelProperties: {
      size: "dense" as const,
      initialCityData: {
        id: hotelData?.id,
        title: hotelData?.title,
        url: `${router.query.cityTitle}/${router.query.hotelTitle}`
      },
      passengerCountInitialValues,
      datePickerInitialValues,
      onClose: handleCloseSearchPanel,
      handleSearchSubmit
    }
  };
};
